import React, { useState, useEffect } from "react";
import { X, Check, Search } from "lucide-react";

const cn = (...classes) => classes.filter(Boolean).join(" ");

export function AttendanceModal({
    isOpen,
    onClose,
    workOrder,
    attendance,
    onSubmit,
    isSubmitting,
}) {
    const [attendanceStatus, setAttendanceStatus] = useState("Present");
    const [hours, setHours] = useState(8);
    const [personCount, setPersonCount] = useState(1);
    const [selectedScopeItems, setSelectedScopeItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (attendance) {
            setAttendanceStatus(attendance.attendance_status);
            setHours(attendance.hours);
            setPersonCount(attendance.person_count);
            // attendance.scope_items might be an array of IDs or objects depending on create vs update
            const initialScopeIds = (attendance.scope_items || []).map(item =>
                typeof item === 'object' ? item.id : item
            ).filter(id => id !== undefined);
            setSelectedScopeItems(initialScopeIds);
        } else {
            setAttendanceStatus("Present");
            setHours(8);
            setPersonCount(1);
            setSelectedScopeItems([]);
        }
    }, [attendance, isOpen]);

    if (!isOpen) return null;

    const scopeItems = workOrder?.scopeItems || [];
    const filteredScopeItems = scopeItems.filter(item =>
        item.item.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleScopeItem = (id) => {
        if (attendanceStatus === "Absent") return;
        setSelectedScopeItems(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const handleApply = () => {
        const data = {
            attendance_status: attendanceStatus,
            hours: parseFloat(hours),
            person_count: parseInt(personCount),
            scope_items: selectedScopeItems,
            date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(), // Today's date in local timezone as per local timezone
        };
        onSubmit(data);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <div>
                        <h2 className="text-lg font-semibold text-foreground">
                            {attendance ? "Update Attendance" : "Mark Attendance"}
                        </h2>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {workOrder?.id} - {workOrder?.projectName}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-muted rounded-full transition-colors"
                    >
                        <X className="h-5 w-5 text-muted-foreground" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-6">
                    {/* Status and Hours */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Attendance</label>
                            <div className="flex p-1 bg-muted rounded-lg border border-border/60">
                                {["Present", "Absent"].map((status) => (
                                    <button
                                        key={status}
                                        onClick={() => {
                                            setAttendanceStatus(status);
                                            if (status === "Absent") {
                                                setPersonCount(0);
                                                setHours(0);
                                                setSelectedScopeItems([]);
                                            }
                                            else {
                                                setPersonCount(1);
                                                setHours(8);
                                            }
                                        }}
                                        className={cn(
                                            "flex-1 py-1.5 text-xs font-semibold rounded-md border transition-all",
                                            attendanceStatus === status
                                                ? status === "Present"
                                                    ? "bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm"
                                                    : "bg-rose-50 border-rose-300 text-rose-700 shadow-sm"
                                                : status === "Present"
                                                    ? "border-transparent text-emerald-700/70 hover:bg-emerald-50/70"
                                                    : "border-transparent text-rose-700/70 hover:bg-rose-50/70"
                                        )}
                                    >
                                        {status}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Hours Worked</label>
                            <input
                                type="number"
                                value={hours}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                        setHours("");
                                    } else {
                                        const parsed = parseFloat(val);
                                        setHours(Math.max(0, parsed));
                                    }
                                }}
                                disabled={attendanceStatus === "Absent"}
                                className="w-full h-9 px-3 bg-white border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sm-primary/20 disabled:bg-muted/50 transition-all"
                                min="0"
                                max="24"
                                step="0.5"
                            />
                        </div>
                    </div>

                    {/* Labour Count */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">Labour Count</label>
                        <input
                            type="number"
                            value={personCount}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "") {
                                    setPersonCount("");
                                } else {
                                    const parsed = parseInt(val);
                                    setPersonCount(Math.max(0, parsed));
                                }
                            }}
                            disabled={attendanceStatus === "Absent"}
                            className="w-full h-9 px-3 bg-white border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sm-primary/20 disabled:bg-muted/50 transition-all"
                            min="1"
                        />
                    </div>

                    {/* Scope Items */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-foreground">Scope Items Worked</label>
                            <span className="text-xs text-muted-foreground">
                                {selectedScopeItems.length} selected
                            </span>
                        </div>

                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search scope items..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-9 pl-9 pr-3 bg-muted/50 border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sm-primary/20 transition-all"
                            />
                        </div>

                        <div className="border border-border/60 rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
                            {filteredScopeItems.length > 0 ? (
                                filteredScopeItems.map((item) => {
                                    const isSelected = selectedScopeItems.includes(item.id);
                                    return (
                                        <div
                                            key={item.id}

                                            onClick={() => toggleScopeItem(item.id)}
                                            className={cn(
                                                "flex items-center justify-between p-3 border-b border-border/40 last:border-0 cursor-pointer transition-colors hover:bg-muted/30",
                                                isSelected && "bg-sm-primary/5"
                                            )}
                                        >
                                            <div className="flex-1 min-w-0 pr-4">
                                                <p className={cn("text-sm font-medium truncate", isSelected ? "text-sm-primary" : "text-foreground")}>
                                                    {item.item}
                                                </p>
                                                {item.description && (
                                                    <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                                                )}
                                            </div>
                                            <div className={cn(
                                                "h-5 w-5 rounded border flex items-center justify-center transition-all",
                                                isSelected
                                                    ? "bg-sm-primary border-sm-primary"
                                                    : "bg-white border-border hover:border-sm-primary/50"
                                            )}>
                                                {isSelected && <Check className="h-3.5 w-3.5 text-white" />}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="p-8 text-center bg-muted/20">
                                    <p className="text-sm text-muted-foreground font-medium">No scope items found</p>
                                    <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-muted/10 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={isSubmitting || (attendanceStatus === "Present" && personCount === 0)}
                        className="px-6 py-2 bg-sm-primary text-white text-sm font-medium rounded-lg shadow hover:bg-sm-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-all"
                    >
                        {isSubmitting ? (
                            <div className="flex items-center gap-2">
                                <div className="h-3 w-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Saving...
                            </div>
                        ) : (
                            attendance ? "Update Attendance" : "Save Attendance"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
